import classNames from "classnames";
import { isEmpty } from "lodash";
import { ChangeEvent, useState } from "react";
import { get, useFormContext } from "react-hook-form";
import { InviteUser } from "../store/generalTypes";
import { LTooltip } from "./LTooltip";

export const LSearchSelect = ({ ...props }: SearchSelectProps): JSX.Element => {
    const {
        register,
        formState: { errors }
    } = useFormContext();
    const error = get(errors, props.inputName);
    const hasError = !isEmpty(error);
    const fieldError = error?.message?.toString() ?? "";
    const [fieldValue, setFieldValue] = useState(props.defaultValue ?? "");

    const classes = classNames(
        "input",
        props.isExpanded ? "is-expanded" : "",
        hasError ? "is-danger" : "",
        props.isRounded ? "is-rounded" : ""
    );
    const labelClasses = classNames(
        "label",
        "is-small",
        hasError ? "is-danger" : "",
        props.theme === "dark" ? "has-text-white" : ""
    );

    const isNumberInput = (e: ChangeEvent<HTMLInputElement>): void => {
        const reg = /^[0-9]+$/;
        if (props.isNumber && !reg.test(e.target.value)) {
            setFieldValue(e.target.value.replace(/[^0-9]/g, ""));
        } else {
            setFieldValue(e.target.value);
        }
    };

    const [isActive, setIsActive] = useState(false);

    const filteredOptions = props.options.filter((option) => {
        let shouldFilter = true;

        shouldFilter = shouldFilter && !props.selected.includes(option.email);

        shouldFilter =
            shouldFilter &&
            (option.firstName.toLowerCase().includes(fieldValue.toLowerCase()) ||
                (option.firstName + " " + option.lastName).toLowerCase().includes(fieldValue.toLowerCase()) ||
                option.lastName.toLowerCase().includes(fieldValue.toLowerCase()) ||
                option.email.toString().includes(fieldValue));

        return shouldFilter;
    });

    const filteredOptionsLength = filteredOptions.length;

    return (
        <div className={`field dropdown ${isActive ? "is-active" : ""}`} style={{ display: "block" }}>
            <label className={labelClasses}>
                {props.label}{" "}
                {props.tooltip && (
                    <span className="ml-2">
                        <LTooltip text={props.tooltip} direction="downRight" />
                    </span>
                )}
            </label>
            <div className="control dropdown-trigger">
                <input
                    type={props.type ?? "text"}
                    className={classes}
                    style={{ fontFamily: "Arial, FontAwesome" }}
                    placeholder={props.placeholder}
                    value={fieldValue}
                    maxLength={props.maxLength}
                    onInput={isNumberInput}
                    {...register(props.inputName, { onChange: props.onChange })}
                    disabled={props.disabled}
                    onFocus={() => setIsActive(true)}
                    onBlur={() => setIsActive(false)}
                />
                <div className="dropdown-menu" id="dropdown-menu" role="menu" style={{ width: "100%" }}>
                    <div className="dropdown-content">
                        {filteredOptions.map((op, idx) => {
                            return (
                                <div key={`dropdown-item-${idx}`}>
                                    <a
                                        className="dropdown-item"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            props.onSelected(op);
                                            setIsActive(false);
                                        }}
                                    >
                                        {props.mapFunction(op)}
                                    </a>
                                    {idx < filteredOptionsLength - 1 && <hr className="dropdown-divider" />}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {hasError && <p className="help is-danger">{fieldError}</p>}
        </div>
    );
};

// TODO: make abstract for type T
export interface SearchSelectProps extends FormFieldProps {
    type?: "email" | "text" | "password" | "number" | "time";
    placeholder?: string;
    isSmall?: boolean; // Specifically for input group
    isLargeLabel?: boolean;
    isExpanded?: boolean;
    isNumber?: boolean;
    theme?: "dark" | "light";
    isRounded?: boolean;
    tooltip?: string;
    disabled?: boolean;
    options: InviteUser[];
    onSelected: (selected: InviteUser) => void;
    selected: string[];
    mapFunction: (user: InviteUser) => JSX.Element;
}

export interface FormFieldProps {
    /** The string label */
    label?: string;
    /** The HTML input `name` attribute */
    inputName: string;
    /** The initial value */
    value?: string;
    /** Sets the initial value of the input */
    defaultValue?: string;
    /** HTML input `onChange` attribute */
    onChange?: React.ChangeEventHandler;
    /** Maximum amount of characters a field will accept */
    maxLength?: number;
}
